import React, { useState, useRef, useEffect } from 'react';
import EmojiPicker, { EmojiClickData, Theme } from 'emoji-picker-react';
import IconEmoji from "@fider/assets/images/heroicons-smile.svg"
import './EmojiPickerModal.scss';
import { Icon } from '../Icon';

interface EmojiPickerModalProps {
  onEmojiSelect?: (emoji: string) => void;
  pickerWidth?: string | number;
  pickerHeight?: string | number;
  theme?: Theme;
  disabled?: boolean;
}

const EmojiPickerModal: React.FC<EmojiPickerModalProps> = ({
  onEmojiSelect,
  pickerWidth = '100%',
  pickerHeight = '400px',
  theme = Theme.AUTO,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  // Handle emoji selection
  const onEmojiClick = (emojiData: EmojiClickData) => {
    //setSelectedEmoji(emojiData.emoji);
    setIsOpen(false);
    
    // Call the callback if provided
    if (onEmojiSelect) {
      onEmojiSelect(emojiData.emoji);
    }
  };

  const toggleModal = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="emoji-picker-container">
      {/* Button to open emoji picker */}
      <button
        onClick={toggleModal}
        className={`c-editor-button ${disabled ? "is-disabled" : ""}`}
        type="button"
        aria-label="Open emoji picker"
        aria-expanded={isOpen}
      >
        <Icon sprite={IconEmoji} />
      </button>

      {/* Modal Overlay */}
      {isOpen && (
        <div 
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="emoji-picker-title"
        >
          <div ref={modalRef} className="modal-content">
            {/* Close button */}
            <button
              onClick={() => setIsOpen(false)}
              className="close-button"
              aria-label="Close emoji picker"
              type="button"
            >
              ×
            </button>

            {/* Title */}
            <div className="modal-header">
              <h2 id="emoji-picker-title">Vælg en Emoji</h2>
            </div>

            {/* Emoji Picker */}
            <div className="picker-wrapper">
              <EmojiPicker
                onEmojiClick={onEmojiClick}
                width={pickerWidth}
                height={pickerHeight}
                theme={theme}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmojiPickerModal;